// ═══════════════════════════════════════════════════════════════
// vaultExportAssets.js — Contenu embarqué dans le ZIP d'export
// ═══════════════════════════════════════════════════════════════
// Trois fichiers texte délivrés à l'utilisateur dans son archive :
//   • decrypt-vault.js  — script Node.js standalone
//   • decrypt-vault.py  — script Python standalone (cryptography)
//   • README.md         — procédure complète, en français
//
// Ces fichiers sont la copie exacte de triia-orchestrator/scripts/
// decrypt-vault.{js,py} (versionnés côté repo). Quand on patche les
// scripts repo, dupliquer les changements ici.
// ═══════════════════════════════════════════════════════════════

export const DECRYPT_VAULT_JS = `#!/usr/bin/env node
// decrypt-vault.js — Déchiffre un export coffre-fort ITRIIA
// Format : itriia-vault-export-v1 (AES-256-GCM + scrypt KDF)
// Usage  : node decrypt-vault.js <fichier.json.enc> <passphrase>
// Aucune dépendance externe — module crypto natif Node >= 14.

const crypto = require('crypto');
const fs = require('fs');
const path = require('path');

const [, , file, passphrase] = process.argv;
if (!file || !passphrase) {
    console.error('Usage : node decrypt-vault.js <fichier.json.enc> <passphrase>');
    process.exit(1);
}
if (!fs.existsSync(file)) {
    console.error(\`Fichier introuvable : \${file}\`);
    process.exit(2);
}

let env;
try { env = JSON.parse(fs.readFileSync(file, 'utf8')); }
catch (e) { console.error('JSON invalide :', e.message); process.exit(3); }

if (env.format !== 'itriia-vault-export-v1') {
    console.error(\`Format inattendu : \${env.format}\`);
    process.exit(4);
}

try {
    const salt    = Buffer.from(env.salt,       'base64');
    const iv      = Buffer.from(env.iv,         'base64');
    const authTag = Buffer.from(env.auth_tag,   'base64');
    const ct      = Buffer.from(env.ciphertext, 'base64');

    const key = crypto.scryptSync(passphrase, salt, 32, { N: 16384, r: 8, p: 1 });
    const decipher = crypto.createDecipheriv('aes-256-gcm', key, iv);
    decipher.setAuthTag(authTag);
    const plaintext = Buffer.concat([decipher.update(ct), decipher.final()]).toString('utf8');

    const data = JSON.parse(plaintext);
    const outFile = file.replace(/\\.json\\.enc$/, '.json');
    fs.writeFileSync(outFile, plaintext);

    console.log(\`✓ Déchiffrement OK\`);
    console.log(\`  \${data.item_count} item(s) — exporté \${data.exported_at}\`);
    console.log(\`  user : \${data.user_id}\`);
    console.log(\`→ Écrit en clair : \${path.resolve(outFile)}\`);
    console.log('');
    console.log('⚠️  Ce fichier contient vos données coffre-fort en clair.');
    console.log('    Effacez-le après usage (shred / rm -P) ou stockez-le');
    console.log('    sur un volume chiffré (LUKS, FileVault, BitLocker).');
} catch (e) {
    if (/bad decrypt|Unsupported state|auth/.test(e.message)) {
        console.error('Échec — passphrase incorrecte ou archive corrompue.');
    } else {
        console.error('Erreur :', e.message);
    }
    process.exit(5);
}
`;

export const DECRYPT_VAULT_PY = `#!/usr/bin/env python3
# decrypt-vault.py — Déchiffre un export coffre-fort ITRIIA
# Format : itriia-vault-export-v1 (AES-256-GCM + scrypt KDF)
# Usage  : python3 decrypt-vault.py <fichier.json.enc> <passphrase>
# Dépendance : pip install cryptography  (Python >= 3.7)

import sys
import json
import base64
import os
from pathlib import Path

try:
    from cryptography.hazmat.primitives.kdf.scrypt import Scrypt
    from cryptography.hazmat.primitives.ciphers.aead import AESGCM
except ImportError:
    print("Dépendance manquante :", file=sys.stderr)
    print("    pip install cryptography", file=sys.stderr)
    sys.exit(1)

if len(sys.argv) != 3:
    print("Usage : python3 decrypt-vault.py <fichier.json.enc> <passphrase>", file=sys.stderr)
    sys.exit(1)

file_path, passphrase = sys.argv[1], sys.argv[2]

if not os.path.isfile(file_path):
    print(f"Fichier introuvable : {file_path}", file=sys.stderr)
    sys.exit(2)

try:
    with open(file_path, 'r', encoding='utf-8') as f:
        env = json.load(f)
except json.JSONDecodeError as e:
    print(f"JSON invalide : {e}", file=sys.stderr)
    sys.exit(3)

if env.get('format') != 'itriia-vault-export-v1':
    print(f"Format inattendu : {env.get('format')}", file=sys.stderr)
    sys.exit(4)

try:
    salt = base64.b64decode(env['salt'])
    iv = base64.b64decode(env['iv'])
    ct = base64.b64decode(env['ciphertext'])
    auth_tag = base64.b64decode(env['auth_tag'])

    key = Scrypt(salt=salt, length=32, n=16384, r=8, p=1).derive(passphrase.encode('utf-8'))
    plaintext = AESGCM(key).decrypt(iv, ct + auth_tag, None).decode('utf-8')

    data = json.loads(plaintext)
    out_file = file_path[:-9] + '.json' if file_path.endswith('.json.enc') else file_path + '.decrypted.json'
    with open(out_file, 'w', encoding='utf-8') as f:
        f.write(plaintext)

    print(f"✓ Déchiffrement OK")
    print(f"  {data['item_count']} item(s) — exporté {data['exported_at']}")
    print(f"  user : {data['user_id']}")
    print(f"→ Écrit en clair : {Path(out_file).resolve()}")
    print()
    print("⚠️  Ce fichier contient vos données coffre-fort en clair.")
    print("    Effacez-le après usage (shred / rm -P) ou stockez-le")
    print("    sur un volume chiffré (LUKS, FileVault, BitLocker).")
except Exception as e:
    if 'InvalidTag' in type(e).__name__:
        print("Échec — passphrase incorrecte ou archive corrompue.", file=sys.stderr)
    else:
        print(f"Erreur : {e}", file=sys.stderr)
    sys.exit(5)
`;

export const VAULT_README_MD = `# Export coffre-fort ITRIIA

Cette archive contient l'intégralité de votre coffre-fort utilisateur,
chiffrée. Sans la passphrase que vous avez copiée au moment de l'export,
le contenu est illisible — y compris pour ITRIIA.

## Que contient cette archive ?

\`\`\`
itriia-vault-export-AAAA-MM-JJ.zip
├── vault.json.enc         — Données du coffre, chiffrées AES-256-GCM
├── decrypt-vault.js       — Script Node.js pour déchiffrer (sans dépendance)
├── decrypt-vault.py       — Script Python pour déchiffrer (cryptography)
└── README.md              — Ce fichier
\`\`\`

## Déchiffrer — option Node.js (recommandée)

Prérequis : Node.js >= 14 installé. Aucun \`npm install\`, le module \`crypto\`
est natif.

\`\`\`bash
node decrypt-vault.js vault.json.enc VOTRE_PASSPHRASE_ICI
\`\`\`

→ Génère un fichier \`vault.json\` à côté, lisible avec n'importe quel
éditeur de texte.

## Déchiffrer — option Python

Prérequis : Python >= 3.7 et \`cryptography\`.

\`\`\`bash
pip install cryptography
python3 decrypt-vault.py vault.json.enc VOTRE_PASSPHRASE_ICI
\`\`\`

## Format du \`vault.json\` après déchiffrement

\`\`\`json
{
  "export_format": "itriia-vault-v1",
  "exported_at": "2026-04-26T...",
  "user_id": "votre-identifiant",
  "item_count": 3,
  "items": [
    {
      "id": 488,
      "project_id": 39,
      "name": "vault-assistant-...txt",
      "role": "user" ou "assistant",
      "source_conversation_id": 1065,
      "created_at": "...",
      "size_bytes": 2772,
      "content": "(votre contenu en clair)"
    },
    ...
  ]
}
\`\`\`

## Sécurité — questions fréquentes

### L'IA d'ITRIIA pourra-t-elle lire ce que je déchiffre localement ?

**Non.** Le fichier \`vault.json\` produit par le script de déchiffrement
existe uniquement sur **votre machine**. Il n'est jamais ré-uploadé à ITRIIA,
jamais envoyé à OpenAI / Anthropic / OpenRouter ou tout autre service
externe. Tant que vous n'ouvrez pas ce fichier dans une application qui
parle elle-même à une IA (par ex. une extension VSCode liée à ChatGPT),
le contenu reste sous votre contrôle exclusif.

### Si je perds la passphrase ?

L'archive devient **illisible**. ITRIIA n'a aucun moyen de la récupérer
— la passphrase n'est jamais conservée côté serveur. Stockez-la dans
un password manager fiable (1Password, Bitwarden, KeePass) ou sur un
support physique en lieu sûr.

### Et si quelqu'un vole ce fichier .zip ?

Sans la passphrase, le contenu est protégé par AES-256-GCM avec une
clé dérivée par scrypt (N=16384, r=8, p=1). Ce niveau résiste à tout
attaque par force brute connue sur du matériel grand public, à condition
que votre passphrase soit forte (les passphrases générées par ITRIIA font
32 octets aléatoires = 256 bits d'entropie, ce qui est inviolable).

### Hygiène après usage

Une fois que vous avez consulté le contenu déchiffré :

\`\`\`bash
# macOS / Linux : effacement sécurisé
shred -u vault.json

# Sinon : suppression simple
rm vault.json
\`\`\`

Ou stockez le fichier déchiffré sur un **volume chiffré** :
- macOS : FileVault, image disque chiffrée
- Linux : LUKS, eCryptfs
- Windows : BitLocker

## Format technique (pour audit)

| Élément | Valeur |
|---|---|
| Algorithme | AES-256-GCM (chiffrement authentifié) |
| KDF | scrypt N=16384 r=8 p=1 |
| Sel | 16 octets aléatoires (uniques par export) |
| IV | 12 octets aléatoires |
| Tag d'authentification | 16 octets (vérifie l'intégrité au déchiffrement) |
| Format envelope | JSON \`itriia-vault-export-v1\` |

Si vous souhaitez vérifier les scripts ou les ré-implémenter, le code
source de référence est dans le dépôt ITRIIA :

\`\`\`
triia-orchestrator/scripts/decrypt-vault.js
triia-orchestrator/scripts/decrypt-vault.py
\`\`\`

---

**Bonnes pratiques** : régénérez une passphrase distincte à chaque
nouvel export. Ne réutilisez jamais une passphrase qui a circulé en
clair (chat, e-mail, capture d'écran). Stockez vos passphrases coffre
séparément de votre mot de passe ITRIIA.
`;
